import { createContext } from "react";

export enum ConfirmationAnswer {
  Yes = "Yes",
  No = "No",
  Skip = "Skip",
}

export enum PrivacyNoticeAnswer {
  Yes = "Yes",
  No = "No",
  None = "None",
}

export type Detail = Record<string, boolean>;

export type PSROutcome = {
  eligible: boolean;
  optedIn: boolean;
};

export type PSRServices = {
  billInAudio: boolean;
  billInBraille: boolean;
  billInLargePrint: boolean;
  passPhrase?: string;
  quarterlyMeterRead: boolean;
  sendKnobTurner: boolean;
};

export type PSRTemporaryNeedsCodes = {
  bereavementDate?: Date | null;
  youngestChildAgeDate?: Date | null;
  livingAloneDate?: Date | null;
  hospitalDate?: Date | null;
};

export type PSR = {
  psrOutcome: PSROutcome;
  psrServices?: PSRServices;
  psrTemporaryNeedsCodes?: PSRTemporaryNeedsCodes;
};

export interface Answers {
  children?: Detail;
  sight?: ConfirmationAnswer;
  sightDetail?: Detail;
  hearing?: ConfirmationAnswer;
  age?: ConfirmationAnswer;
  ageDetail?: Detail;
  ageCare?: ConfirmationAnswer;
  medicalEquipment?: ConfirmationAnswer;
  medicalEquipmentDetail?: Detail;
  miscellaneous?: Detail;
  chronicIllness?: ConfirmationAnswer;
  chronicIllnessDetail?: Detail;
  financial?: ConfirmationAnswer;
  financialDetail?: Detail;
  dailyActivity?: ConfirmationAnswer;
  dailyActivityDetail?: string;
  dailySupport?: ConfirmationAnswer;
  dailySupportDetail?: string;
  psr?: PSR;
}

export type MappedPsrAnswer<T> = Required<{
  [P in keyof T]: boolean;
}>;

export type ConfirmationAnswerWithDetails =
  | Omit<ConfirmationAnswer, "Yes">
  | Record<string, boolean>
  | string[];

export type PSRConfirmationAnswerWithDetails =
  | Record<string, string>
  | string[];

export interface MappedAnswers {
  children?: ConfirmationAnswer | string;
  sight?: ConfirmationAnswerWithDetails;
  hearing?: ConfirmationAnswer | string;
  age?: ConfirmationAnswerWithDetails;
  ageCare?: ConfirmationAnswer;
  medicalEquipment?: ConfirmationAnswerWithDetails;
  miscellaneous?: Record<string, boolean>;
  chronicIllness?: ConfirmationAnswerWithDetails;
  financial?: ConfirmationAnswerWithDetails;
  dailyActivity?: ConfirmationAnswerWithDetails;
  dailySupport?: ConfirmationAnswerWithDetails;
  psrServices?: PSRConfirmationAnswerWithDetails;
  psrOutcome?: PSRConfirmationAnswerWithDetails;
  bereavementDate?: Record<string, string>;
  youngestChildAgeDate?: Record<string, string>;
  hospitalDate?: Record<string, string>;
  livingAloneDate?: Record<string, string>;
}

export type TemporaryNeed = {
  question: string;
  reason: string;
};

export interface TemporaryNeedsQuestions {
  bereavement?: TemporaryNeed;
  childUnder5?: TemporaryNeed;
  livingAloneUnder18?: TemporaryNeed;
  beenInHospital?: TemporaryNeed;
}

export interface VulnerabilityContextType {
  privacyNoticeAnswer: PrivacyNoticeAnswer;
  setPrivacyNoticeAnswer: React.Dispatch<
    React.SetStateAction<PrivacyNoticeAnswer>
  >;
  answers: Answers;
  setAnswers: React.Dispatch<React.SetStateAction<Answers>>;
  submitted: boolean;
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
}

const VulnerabilityContext = createContext<VulnerabilityContextType | null>(
  null,
);

export default VulnerabilityContext;
